import type { Backend } from '@/lib/backend-cors/backend.ts'
import type { BankIDDataResponse, CreateImageCollectionResponse, CreateImageResponse, ElhubConsentData, ElhubConsentResponse, OrderResponse, UIResponse } from '@/lib/backend-cors/types.ts'
import type { MpidRequest, MpidResult, Product } from '@Visma-Real-Estate-Solutions/acquisit-ui-vue/library'
import { Origin } from '@/stores/generic'
import { useAxios } from '@/lib/backend-cors/axios'
import type { ProtocolSubmission } from '@/stores/protocol.ts'
import type { AxiosResponse } from 'axios'
import type { Backup } from '@/stores/backup.ts'

export const useAsgard = (): Backend => {
	const axios = useAxios({
		name: 'asgard',
		baseURL: import.meta.env.ACQUISIT_ASGARD_URL
	})
	
	const getProtocolOrigin = async (...args: string[]): Promise<Origin> => {
		return Origin.CLASSIC
	}
	
	return {
		get label(): string {
			return axios.name
		},
		
		get baseURL(): string {
			return axios.baseURL
		},
		
		retry: axios.retry,
		
		// Protocols
		getProtocolOrigin,
		
		async getOrderByTicket(ticketID: string, password: string): Promise<OrderResponse> {
			const response = await axios.request<OrderResponse>('get', '/order/' + ticketID + '/' + password + '?ts=' + (+new Date()), {}, {}, {
				'X-Navigator': navigator.userAgent,
				'X-Acquisit-Host': window.location.host
			}, false)
			
			return response.data
		},
		
		async getOrderByToken(token: string): Promise<OrderResponse> {
			const response = await axios.request<OrderResponse>('get', '/order/' + token + '?ts=' + (+new Date()), {}, {}, {
				'X-Navigator': navigator.userAgent,
				'X-Acquisit-Host': window.location.host
			}, false)
			
			return response.data
		},
		
		async getUI(token: string): Promise<UIResponse> {
			const response = await axios.request<UIResponse>('get', '/order/' + token + '/ui?ts=' + (+new Date()), {}, {}, {
				'X-Navigator': navigator.userAgent,
				'X-Acquisit-Host': window.location.host
			}, false)
			
			return response.data
		},
		
		async submitProtocol(token: string, payload: Record<string, any>): Promise<AxiosResponse> {
			return await axios.request('put', '/order/' + token + '/protocol', {}, payload, {
				'X-Acquisit-Host': window.location.host
			}, true)
		},
		
		async createConsent(token: string, submission: ProtocolSubmission, returnURL: string): Promise<ElhubConsentResponse> {
			return {
				type: 'signing',
				url: returnURL + '?status=success&signicatSessionId=dummy-session-asgard'
			} as ElhubConsentResponse
		},
		
		async getConsentData(token: string, sessionID: string): Promise<ElhubConsentData> {
			return {
				message: 'Avtalekontroll fra Elhub er signert og godkjent, du kan gå videre til neste side.',
				protocol_signatures: [
					{
						id: 1,
						mpid: '707057500012345678',
						elhub_consent_id: 'consent-dummy-asgard',
						signicat_status: 'signed',
						protocol_file_id: 1
					}
				]
			}
		},
		
		// Backups
		
		async getLatestBackup(token: string): Promise<Record<string, any> | null> {
			const response = await axios.request('get', '/backups/' + token + '/latest?ts=' + (+new Date()), {}, {}, {
				'X-Navigator': navigator.userAgent,
				'X-Acquisit-Host': window.location.host
			}, false)
			
			return response.data
		},
		
		async storeBackup(token: string, backup: Backup): Promise<void> {
			await axios.put('/backups/' + token, {}, backup)
		},
		
		async deleteBackup(token: string): Promise<void> {
			await axios.delete('/backups/' + token)
		},
		
		// Images
		
		async createImageCollection(
			token: string,
			title: string | null,
			semantic: string | null,
			metadata: Record<string, any> | null
		): Promise<CreateImageCollectionResponse> {
			const response = await axios.put<CreateImageCollectionResponse>('/order/' + token + '/collection', {}, {
				title,
				semantic,
				metadata
			})
			
			return response.data
		},
		
		async updateImageCollection(
			token: string,
			imageCollectionID: string | number,
			title: string | null,
			metadata: Record<string, any> | null
		): Promise<void> {
			await axios.patch('/order/' + token + '/collections/' + imageCollectionID, {}, {
				title,
				metadata
			})
		},
		
		async uploadImage(
			token: string,
			imageCollectionID: string | number,
			image: string,
			title: string | null
		): Promise<CreateImageResponse> {
			const response = await axios.put<CreateImageResponse>('/order/' + token + '/collections/' + imageCollectionID + '/image', {}, {
				image,
				title
			})
			
			return response.data
		},
		
		async deleteImageCollection(token: string, imageCollectionID: string | number): Promise<void> {
			await axios.delete('/order/' + token + '/collections/' + imageCollectionID)
		},
		
		async deleteImage(token: string, imageCollectionID: string | number, imageID: string | number): Promise<void> {
			await axios.delete('/order/' + token + '/collections/' + imageCollectionID + '/image/' + imageID)
		},
		
		// BankID
		
		getBankIDURL(token: string, personID: string, personNumber: string, returnURL: string): string {
			const url = new URL(window.location.href)
			url.pathname = '/bankid-dummy.html'
			url.searchParams.set('protocolUrl', returnURL)
			url.searchParams.set('personNumber', personNumber)
			url.searchParams.set('personId', personID)
			
			return url.href
		},
		
		async getBankIDData(token: string, personID: string, sessionID: string): Promise<BankIDDataResponse> {
			return {
				common_name: 'Sample Person',
				person_number: '010490-12345',
				timestamp: Math.floor((+new Date()) / 1000)
			}
		},
		
		async fetchMpid(token: string, request: MpidRequest): Promise<MpidResult[]> {
			return []
		},
		
		// Products
		
		async getProductTerms(token: string, product: Product): Promise<string | null> {
			const productID = product.product_id ?? product.uid
			
			const response = await axios.request('get', '/order/' + token + '/products/' + productID + '/terms?ts=' + (+new Date()), {}, {}, {
				'Content-Type': 'text/plain'
			}, false)
			
			return response.data
		},
		
		// Reviews
		
		async submitReview(token: string, score: number, comment: string): Promise<void> {
			await axios.put('/order/' + token + '/review', {}, {
				score,
				comment
			})
		},
		
		parseError(error: any): any {
			return axios.parseError(error)
		},
		
		onError(error: any): any {
			return axios.onError(error)
		},
	}
}